import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  clearOnboardingReturn,
  peekOnboardingReturn,
} from './onboarding-return';

/**
 * Where connectors' sign-ins land the user once they finish. Exact paths: a
 * provider's `/integrations/<name>/callback` page is still completing the
 * sign-in, and moves on to `/integrations` itself when it's done.
 */
const CONNECT_LANDINGS = new Set([
  '/integrations',
  '/settings/email-accounts',
  '/sequencing/settings',
]);

/** Stripe's `?trial=` results that leave no trial: a closed Checkout, a
 *  failure, or a card the API refused (prepaid, or used on another account). */
const TRIAL_NOT_STARTED = new Set([
  'cancelled',
  'error',
  'card_in_use',
  'prepaid_card',
]);

/**
 * Brings a user back into onboarding after another site: a connector's
 * sign-in (back to the tools step) or Stripe Checkout (on to the customize
 * step once the trial started, back to the trial page when it was closed).
 * Mounted once at the root; does nothing without the marker the step set
 * before leaving.
 */
export function useOnboardingReturnRedirect() {
  const navigate = useNavigate();
  // Where the user has landed: the resolved location, set once the page has
  // rendered, not one still loading (that would read a redirect as arrived).
  const pathname = useRouterState({
    select: (s) => (s.resolvedLocation ?? s.location).pathname,
  });
  const href = useRouterState({
    select: (s) => (s.resolvedLocation ?? s.location).href,
  });
  // The page as it loaded, read while rendering: Stripe's result is in the
  // URL only until the trial boundary's effect (a child's, so it runs before
  // this hook's) drops `?trial=`.
  const [arrival] = useState(() => ({
    pathname: window.location.pathname,
    params: new URLSearchParams(window.location.search),
  }));

  useEffect(() => {
    // A hosted sign-in popup only reports to its opener. Its copied
    // onboarding marker belongs to the main window and must stay there.
    if (
      pathname === '/settings/email-accounts' &&
      new URLSearchParams(window.location.search).has('popup')
    )
      return;
    const target = peekOnboardingReturn();
    if (!target) return;

    if (target === 'connect') {
      const path = pathname.replace(/\/+$/, '') || '/';
      if (path === '/onboarding') {
        clearOnboardingReturn(); // back on the tools step
        return;
      }
      if (!CONNECT_LANDINGS.has(path)) return;
      // The marker stays until the tools step is on screen: Integrations
      // drops a sign-in's result from its URL once it has toasted it, which
      // can land after this navigation and bring the user back here.
      void navigate({
        to: '/onboarding',
        search: { step: 'connect' },
        replace: true,
      });
      return;
    }

    if (target === 'trial') {
      const params =
        pathname === arrival.pathname
          ? arrival.params
          : new URLSearchParams(window.location.search);
      const trial = params.get('trial');
      const closed =
        (pathname === '/' && trial !== null && TRIAL_NOT_STARTED.has(trial)) ||
        (pathname.startsWith('/plans') && params.get('status') === 'cancelled');
      if (pathname === '/' && trial === 'activated') {
        clearOnboardingReturn();
        void navigate({
          to: '/onboarding',
          search: { step: 'customize' },
          replace: true,
        });
      } else if (closed) {
        clearOnboardingReturn();
        void navigate({
          to: '/onboarding',
          search: { step: 'trial' },
          replace: true,
        });
      }
    }
  }, [pathname, href, navigate, arrival]);
}
